import axios, { type AxiosInstance } from "axios";
import client from "./client";

/**
 * A client of its own, because the shared one registers `axiosRetry(client, { retries: 3 })`.
 *
 * Minting is not idempotent: every attempt that reaches the server reserves an upload at
 * Cloudflare *and* writes a `review_media` row, so a response lost on the way back would leave
 * abandoned assets sitting until the prune sweep. Retrying safely would need an idempotency key
 * the endpoint honours.
 */
const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

const uploadClient: AxiosInstance = axios.create();
uploadClient.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";

export async function voteReview(reviewId: number, helpful: boolean) {
    const response = await client.post(`/reviews/${reviewId}/vote`, {
        helpful: helpful,
    });

    return response.data;
}

export async function reportReview(reviewId: number) {
    const response = await client.post(`/reviews/${reviewId}/report`);

    return response.data;
}

export async function recordReviewRating(inputs: Record<string, unknown>) {
    const response = await client.post("/reviews/rating", inputs);

    return response.data;
}

/**
 * Reserve a one-time Cloudflare upload URL for one photo.
 *
 * The token travels in the body rather than a header: this project wires no X-CSRF-TOKEN header
 * and renders no csrf-token meta tag, so a `web`-group route answers 419 without it.
 */
export async function requestReviewPhotoUploadUrl(csrf: string) {
    const response = await uploadClient.post("/reviews/media/upload-url", {
        _token: csrf,
    });

    return response.data;
}

/**
 * Reserve a one-time Cloudflare upload URL for one video.
 *
 * The token travels in the body for the reason the photo mint's does, and the reservation this
 * one abandons is dearer: a mint holds maxDurationSeconds of Stream storage, and bills for it,
 * whether or not a file ever follows.
 */
export async function requestReviewVideoUploadUrl(csrf: string) {
    const response = await uploadClient.post(
        "/reviews/media/video-upload-url",
        { _token: csrf },
    );

    return response.data;
}

/**
 * Post the file to the one-time Cloudflare URL the server minted.
 *
 * Off the shared axios client for the same reason as the mint, and doubly so here: its global
 * axiosRetry would re-send the whole body up to four times on one timeout, against a URL that may
 * only be used once — and a video body is measured in megabytes. XMLHttpRequest rather than fetch
 * because fetch reports no request-body progress at this project's browser floor, and a minute-long
 * clip over cellular behind a static screen reads as broken.
 */
export function uploadReviewMedia(
    uploadUrl: string,
    blob: Blob,
    filename: string,
    onProgress: (percent: number) => void,
): Promise<void> {
    return new Promise((resolve, reject) => {
        const body = new FormData();
        // Cloudflare's direct-upload endpoints read the file from a field with this exact name.
        body.append("file", blob, filename);

        const request = new XMLHttpRequest();
        request.open("POST", uploadUrl);
        // A connection an intermediary accepts and then never answers would otherwise leave this
        // promise unsettled for good, holding the form's Save button down with nothing on screen to
        // explain it. Generous rather than tight, because the body can be a minute of video on a
        // slow link, and the point is to bound a hang and not to police a slow upload.
        request.timeout = UPLOAD_TIMEOUT_MS;
        request.ontimeout = () =>
            reject(new Error("The upload did not finish. Please try again."));
        request.upload.onprogress = (event: ProgressEvent) => {
            if (event.lengthComputable) {
                onProgress(Math.round((event.loaded / event.total) * 100));
            }
        };
        request.onload = () =>
            request.status >= 200 && request.status < 300
                ? resolve()
                : reject(
                      new Error("The upload did not finish. Please try again."),
                  );
        request.onerror = () =>
            reject(new Error("The upload did not finish. Please try again."));
        request.send(body);
    });
}
